import React, { useState, useEffect } from 'react';

// ─── A-Level subject metadata ─────────────────────────────────────────────────

const ALEVEL_SUBJECTS = {
  geography:     { label: 'Geography',              color: 'text-blue-400',  badge: 'bg-blue-400/10 text-blue-400 border-blue-400/20',  dot: 'bg-blue-400',  activeBorder: 'border-blue-400/40'  },
  'env-science': { label: 'Environmental Science',  color: 'text-green-400', badge: 'bg-green-400/10 text-green-400 border-green-400/20', dot: 'bg-green-400', activeBorder: 'border-green-400/40' },
};

const ALEVEL_SUBJECTS_WITH_PAST_PAPERS = new Set(['geography', 'env-science']);

// ─── A-Level Geography topic metadata ─────────────────────────────────────────

const ALEVEL_TOPIC_DISPLAY = {
  water_carbon:      'Water & Carbon',
  hazards:           'Hazards',
  deserts:           'Deserts',
  coastal:           'Coastal Landscapes',
  glacial:           'Glaciated Landscapes',
  changing_places:   'Changing Places',
  governance:        'Governance',
  resource_security: 'Resource Security',
};

const ALEVEL_TOPIC_COLORS = {
  water_carbon:      'text-blue-400',
  hazards:           'text-red-400',
  deserts:           'text-yellow-400',
  coastal:           'text-cyan-400',
  glacial:           'text-sky-400',
  changing_places:   'text-orange-400',
  governance:        'text-purple-400',
  resource_security: 'text-green-400',
};

const ALEVEL_TOPIC_BADGES = {
  water_carbon:      'bg-blue-400/10 text-blue-400 border-blue-400/30',
  hazards:           'bg-red-400/10 text-red-400 border-red-400/30',
  deserts:           'bg-yellow-400/10 text-yellow-400 border-yellow-400/30',
  coastal:           'bg-cyan-400/10 text-cyan-400 border-cyan-400/30',
  glacial:           'bg-sky-400/10 text-sky-400 border-sky-400/30',
  changing_places:   'bg-orange-400/10 text-orange-400 border-orange-400/30',
  governance:        'bg-purple-400/10 text-purple-400 border-purple-400/30',
  resource_security: 'bg-green-400/10 text-green-400 border-green-400/30',
};

// ─── A-Level Environmental Science topic metadata ──────────────────────────────

const ENV_SCIENCE_TOPIC_DISPLAY = {
  // 3.1 The living environment
  '3.1.1': 'Conditions for life on Earth',
  '3.1.2': 'Conservation of biodiversity',
  '3.1.3': 'Life processes in the biosphere and conservation planning',
  // 3.2 The physical environment
  '3.2.1': 'The atmosphere',
  '3.2.2': 'The hydrosphere',
  '3.2.3': 'Mineral resources',
  '3.2.4': 'Biogeochemical cycles',
  '3.2.5': 'Soils',
  // 3.3 Energy resources
  '3.3.1': 'The importance of energy supplies in the development of society',
  '3.3.2': 'The impact of the features of energy resources on their use',
  '3.3.3': 'The sustainability of current energy resource exploitation',
  '3.3.4': 'Strategies to secure future energy supplies',
  // 3.4 Pollution
  '3.4.1': 'The properties of pollutants',
  '3.4.2': 'How environmental features affect the severity of pollution',
  '3.4.3': 'Strategies to control pollutants based on their properties and features of the environment',
  // 3.5 Biological resources
  '3.5.1': 'Agriculture',
  '3.5.2': 'Aquatic food production systems',
  '3.5.3': 'Forest resources',
  // 3.6 Sustainability
  '3.6.1': 'Dynamic equilibria',
  '3.6.2': 'Energy',
  '3.6.3': 'Material cycles',
  '3.6.4': 'The circular economy',
  // 3.7 Research methods
  '3.7.1': 'Scientific methodologies',
  '3.7.2': 'Sampling techniques',
};

const ENV_SCIENCE_TOPIC_COLORS = {
  // 3.1 — greens
  '3.1.1': 'text-emerald-400',
  '3.1.2': 'text-green-400',
  '3.1.3': 'text-teal-400',
  // 3.2 — sky/earth tones
  '3.2.1': 'text-sky-400',
  '3.2.2': 'text-cyan-400',
  '3.2.3': 'text-stone-400',
  '3.2.4': 'text-lime-400',
  '3.2.5': 'text-amber-400',
  // 3.3 — oranges/yellows
  '3.3.1': 'text-orange-400',
  '3.3.2': 'text-yellow-400',
  '3.3.3': 'text-orange-400',
  '3.3.4': 'text-amber-400',
  // 3.4 — reds/pinks
  '3.4.1': 'text-red-400',
  '3.4.2': 'text-rose-400',
  '3.4.3': 'text-pink-400',
  // 3.5 — warm greens
  '3.5.1': 'text-lime-400',
  '3.5.2': 'text-teal-400',
  '3.5.3': 'text-green-400',
  // 3.6 — purples
  '3.6.1': 'text-purple-400',
  '3.6.2': 'text-violet-400',
  '3.6.3': 'text-indigo-400',
  '3.6.4': 'text-blue-400',
  // 3.7 — slate
  '3.7.1': 'text-slate-400',
  '3.7.2': 'text-slate-400',
};

const ENV_SCIENCE_TOPIC_BADGES = {
  // 3.1
  '3.1.1': 'bg-emerald-400/10 text-emerald-400 border-emerald-400/30',
  '3.1.2': 'bg-green-400/10 text-green-400 border-green-400/30',
  '3.1.3': 'bg-teal-400/10 text-teal-400 border-teal-400/30',
  // 3.2
  '3.2.1': 'bg-sky-400/10 text-sky-400 border-sky-400/30',
  '3.2.2': 'bg-cyan-400/10 text-cyan-400 border-cyan-400/30',
  '3.2.3': 'bg-stone-400/10 text-stone-400 border-stone-400/30',
  '3.2.4': 'bg-lime-400/10 text-lime-400 border-lime-400/30',
  '3.2.5': 'bg-amber-400/10 text-amber-400 border-amber-400/30',
  // 3.3
  '3.3.1': 'bg-orange-400/10 text-orange-400 border-orange-400/30',
  '3.3.2': 'bg-yellow-400/10 text-yellow-400 border-yellow-400/30',
  '3.3.3': 'bg-orange-400/10 text-orange-400 border-orange-400/30',
  '3.3.4': 'bg-amber-400/10 text-amber-400 border-amber-400/30',
  // 3.4
  '3.4.1': 'bg-red-400/10 text-red-400 border-red-400/30',
  '3.4.2': 'bg-rose-400/10 text-rose-400 border-rose-400/30',
  '3.4.3': 'bg-pink-400/10 text-pink-400 border-pink-400/30',
  // 3.5
  '3.5.1': 'bg-lime-400/10 text-lime-400 border-lime-400/30',
  '3.5.2': 'bg-teal-400/10 text-teal-400 border-teal-400/30',
  '3.5.3': 'bg-green-400/10 text-green-400 border-green-400/30',
  // 3.6
  '3.6.1': 'bg-purple-400/10 text-purple-400 border-purple-400/30',
  '3.6.2': 'bg-violet-400/10 text-violet-400 border-violet-400/30',
  '3.6.3': 'bg-indigo-400/10 text-indigo-400 border-indigo-400/30',
  '3.6.4': 'bg-blue-400/10 text-blue-400 border-blue-400/30',
  // 3.7
  '3.7.1': 'bg-slate-400/10 text-slate-400 border-slate-400/30',
  '3.7.2': 'bg-slate-400/10 text-slate-400 border-slate-400/30',
};

const ENV_SCIENCE_SECTIONS = [
  { key: '3.1', label: 'The living environment',   color: 'text-emerald-400', badge: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/30', topics: ['3.1.1','3.1.2','3.1.3'] },
  { key: '3.2', label: 'The physical environment', color: 'text-sky-400',     badge: 'bg-sky-400/10 text-sky-400 border-sky-400/30',             topics: ['3.2.1','3.2.2','3.2.3','3.2.4','3.2.5'] },
  { key: '3.3', label: 'Energy resources',         color: 'text-orange-400',  badge: 'bg-orange-400/10 text-orange-400 border-orange-400/30',    topics: ['3.3.1','3.3.2','3.3.3','3.3.4'] },
  { key: '3.4', label: 'Pollution',                color: 'text-red-400',     badge: 'bg-red-400/10 text-red-400 border-red-400/30',             topics: ['3.4.1','3.4.2','3.4.3'] },
  { key: '3.5', label: 'Biological resources',     color: 'text-lime-400',    badge: 'bg-lime-400/10 text-lime-400 border-lime-400/30',          topics: ['3.5.1','3.5.2','3.5.3'] },
  { key: '3.6', label: 'Sustainability',           color: 'text-purple-400',  badge: 'bg-purple-400/10 text-purple-400 border-purple-400/30',    topics: ['3.6.1','3.6.2','3.6.3','3.6.4'] },
  { key: '3.7', label: 'Research methods',         color: 'text-slate-400',   badge: 'bg-slate-400/10 text-slate-400 border-slate-400/30',       topics: ['3.7.1','3.7.2'] },
];

const ENV_SCIENCE_ARCHETYPE_PILLS = [
  {
    id:    'envsci_short',
    marks: '1–5',
    label: 'SHORT',
    active:   'border-green-500/60 bg-green-500/20 text-green-300',
    inactive: 'border-[#30363d] text-[#484f58] hover:text-green-500 hover:border-green-500/40',
  },
  {
    id:    'envsci_9',
    marks: 9,
    label: 'MARK',
    active:   'border-amber-500/60 bg-amber-500/20 text-amber-300',
    inactive: 'border-[#30363d] text-[#484f58] hover:text-amber-500 hover:border-amber-500/40',
  },
  {
    id:    'envsci_25',
    marks: 25,
    label: 'ESSAY',
    active:   'border-purple-500/60 bg-purple-500/20 text-purple-300',
    inactive: 'border-[#30363d] text-[#484f58] hover:text-purple-500 hover:border-purple-500/40',
  },
];

// ─── GCSE Psychology topic metadata ───────────────────────────────────────────

const GCSE_SUBJECTS = {
  biology:    { label: 'Biology',    color: 'text-green-400', badge: 'bg-green-400/10 text-green-400 border-green-400/20',  dot: 'bg-green-400',  activeBorder: 'border-green-400/40'  },
  chemistry:  { label: 'Chemistry',  color: 'text-teal-400',  badge: 'bg-teal-400/10 text-teal-400 border-teal-400/20',    dot: 'bg-teal-400',   activeBorder: 'border-teal-400/40'   },
  physics:    { label: 'Physics',    color: 'text-amber-400', badge: 'bg-amber-400/10 text-amber-400 border-amber-400/20',  dot: 'bg-amber-400',  activeBorder: 'border-amber-400/40'  },
  psychology: { label: 'Psychology', color: 'text-pink-400',  badge: 'bg-pink-400/10 text-pink-400 border-pink-400/20',    dot: 'bg-pink-400',   activeBorder: 'border-pink-400/40'   },
};

// Subjects that have written past-paper questions in the sidebar list
const GCSE_SUBJECTS_WITH_PAST_PAPERS = new Set(['psychology', 'biology', 'chemistry', 'physics']);
const GCSE_SCIENCE_SUBJECTS          = new Set(['biology', 'chemistry', 'physics']);

const GCSE_TOPIC_DISPLAY = {
  social_influence: 'Social Influence',
  memory:           'Memory',
  perception:       'Perception',
  development:      'Development',
  attachment:       'Attachment',
  psychopathology:  'Psychopathology',
  approaches:       'Approaches',
  biopsychology:    'Biopsychology',
  research_methods: 'Research Methods',
  // GCSE Science (AQA 8464 Combined Science: Trilogy). The B/C/P + number prefix
  // is stripped here so the sidebar shows only the topic name; ordering is
  // restored by gcseScienceTopicOrder() below.
  'B1 Cell biology':                                     'Cell biology',
  'B2 Organisation':                                     'Organisation',
  'B3 Infection and response':                           'Infection and response',
  'B4 Bioenergetics':                                    'Bioenergetics',
  'B5 Homeostasis and response':                         'Homeostasis and response',
  'B6 Inheritance, variation and evolution':             'Inheritance, variation and evolution',
  'B7 Ecology':                                          'Ecology',
  'C8 Atomic structure and the periodic table':          'Atomic structure & periodic table',
  'C9 Bonding, structure, and the properties of matter': 'Bonding & structure',
  'C10 Quantitative chemistry':                          'Quantitative chemistry',
  'C11 Chemical changes':                                'Chemical changes',
  'C12 Energy changes':                                  'Energy changes',
  'C13 The rate and extent of chemical change':          'Rate & extent of change',
  'C14 Organic chemistry':                               'Organic chemistry',
  'C15 Chemical analysis':                               'Chemical analysis',
  'C16 Chemistry of the atmosphere':                     'Chemistry of the atmosphere',
  'C17 Using resources':                                 'Using resources',
  'P18 Energy':                                          'Energy',
  'P19 Electricity':                                     'Electricity',
  'P20 Particle model of matter':                        'Particle model of matter',
  'P21 Atomic structure':                                'Atomic structure',
  'P22 Forces':                                          'Forces',
  'P23 Waves':                                           'Waves',
  'P24 Magnetism and electromagnetism':                  'Magnetism & electromagnetism',
};

// AQA 8464 numbers topics continuously across subjects (B1–B7, C8–C17, P18–P24).
// Returns the leading numeric prefix so topics sort numerically in the sidebar,
// or Infinity for non-science topics so they keep their source order.
function gcseScienceTopicOrder(topic) {
  const match = /^[BCP](\d+)/.exec(topic);
  return match ? Number(match[1]) : Infinity;
}

// Sidebar question label, e.g. "2018 · Specimen · Q1 · 1 mark" (specimen) or
// "2018 · Q1 · 1 mark" (real past papers). Skips empty segments so older
// content (no year / no paper_type) still renders. "live" is the pipeline's
// flag for real exam papers; it's noise to a student, so omit it.
function formatQuestionLabel(q, index) {
  const parts = [];
  if (q.year) parts.push(q.year);
  if (q.paper_type && q.paper_type !== 'live') {
    parts.push(q.paper_type[0].toUpperCase() + q.paper_type.slice(1));
  }
  parts.push(`Q${index + 1}`);
  if (q.marks) parts.push(`${q.marks} mark${q.marks === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

// GCSE Science topics are colour-coded by subject prefix (B*/C*/P*) via a fallback
// in topicColor()/topicBadge() below, so only the psychology topics need explicit entries.
const GCSE_TOPIC_COLORS = {
  social_influence: 'text-purple-400',
  memory:           'text-blue-400',
  perception:       'text-teal-400',
  development:      'text-lime-400',
  attachment:       'text-rose-400',
  psychopathology:  'text-amber-400',
  approaches:       'text-green-400',
  biopsychology:    'text-cyan-400',
  research_methods: 'text-slate-400',
};

const GCSE_TOPIC_BADGES = {
  social_influence: 'bg-purple-400/10 text-purple-400 border-purple-400/30',
  memory:           'bg-blue-400/10 text-blue-400 border-blue-400/30',
  perception:       'bg-teal-400/10 text-teal-400 border-teal-400/30',
  development:      'bg-lime-400/10 text-lime-400 border-lime-400/30',
  attachment:       'bg-rose-400/10 text-rose-400 border-rose-400/30',
  psychopathology:  'bg-amber-400/10 text-amber-400 border-amber-400/30',
  approaches:       'bg-green-400/10 text-green-400 border-green-400/30',
  biopsychology:    'bg-cyan-400/10 text-cyan-400 border-cyan-400/30',
  research_methods: 'bg-slate-400/10 text-slate-400 border-slate-400/30',
};

// Per-subject colour fallback for GCSE Science topics (B1 → green, C8 → amber, P18 → cyan).
const SCIENCE_TOPIC_COLOR_BY_PREFIX = { B: 'text-green-400', C: 'text-amber-400', P: 'text-cyan-400' };
const SCIENCE_TOPIC_BADGE_BY_PREFIX = {
  B: 'bg-green-400/10 text-green-400 border-green-400/30',
  C: 'bg-amber-400/10 text-amber-400 border-amber-400/30',
  P: 'bg-cyan-400/10 text-cyan-400 border-cyan-400/30',
};

// ─── Archetype pills ───────────────────────────────────────────────────────────

const ALEVEL_ARCHETYPE_PILLS = [
  {
    id:    'outline_4',
    marks: 4,
    label: 'MARK',
    active:   'border-slate-500/60 bg-slate-500/20 text-slate-300',
    inactive: 'border-[#30363d] text-[#484f58] hover:text-slate-500 hover:border-slate-500/40',
  },
  {
    id:    'data_6',
    marks: 6,
    label: 'DATA',
    active:   'border-sky-500/60 bg-sky-500/20 text-sky-300',
    inactive: 'border-[#30363d] text-[#484f58] hover:text-sky-500 hover:border-sky-500/40',
  },
  {
    id:    'assess_6',
    marks: 6,
    label: 'ASSESS',
    active:   'border-amber-500/60 bg-amber-500/20 text-amber-300',
    inactive: 'border-[#30363d] text-[#484f58] hover:text-amber-500 hover:border-amber-500/40',
  },
  {
    id:    'scaffold_9',
    marks: 9,
    label: 'MARK',
    active:   'border-violet-500/60 bg-violet-500/20 text-violet-300',
    inactive: 'border-[#30363d] text-[#484f58] hover:text-violet-500 hover:border-violet-500/40',
  },
  {
    id:    'essay_20',
    marks: 20,
    label: 'ESSAY',
    active:   'border-rose-500/60 bg-rose-500/20 text-rose-300',
    inactive: 'border-[#30363d] text-[#484f58] hover:text-rose-500 hover:border-rose-500/40',
  },
];

const GCSE_ARCHETYPE_PILLS = [
  {
    id:    'past_papers',
    marks: null,
    label: 'PAST PAPERS',
    active:   'border-violet-500/60 bg-violet-500/20 text-violet-300',
    inactive: 'border-[#30363d] text-[#484f58] hover:text-violet-500 hover:border-violet-500/40',
  },
];

const MODEL_OPTIONS = [
  { id: 'claude', label: 'Claude Sonnet 4.6' },
  { id: 'gemini', label: 'Gemini 2.5 Flash' },
];

export default function Sidebar({
  selectedQuestion,
  onSelect,
  markType,
  onMarkTypeChange,
  selectedArchetype,
  onArchetypeChange,
  activeProvider,
  onProviderChange,
  isAdmin = false,
  onOpenDashboard,
  onOpenMockExam,
  onOpenExamHistory,
  onOpenHistory,
  onOpenQuiz,
  onOpenLeaderboard,
  onOpenScoreReview,
  markedQuestionIds = new Set(),
  isTyping = false,
  guidedMode = true,
  onGuidedModeChange,
  totalPoints = 0,
  todayPoints = null,
  currentStreak = 0,
  questionBestScores = {},
  examLevel = 'a-level',
  onExamLevelChange,
  gcseSubject = 'psychology',
  onGcseSubjectChange,
  aLevelSubject = 'geography',
  onALevelSubjectChange,
  activeQuestions = [],
}) {
  const isGcse        = examLevel === 'gcse';
  const hasPastPapers = isGcse
    ? GCSE_SUBJECTS_WITH_PAST_PAPERS.has(gcseSubject)
    : ALEVEL_SUBJECTS_WITH_PAST_PAPERS.has(aLevelSubject);
  const isEnvSci     = !isGcse && aLevelSubject === 'env-science';
  const archetypePills = isGcse ? GCSE_ARCHETYPE_PILLS
    : isEnvSci ? ENV_SCIENCE_ARCHETYPE_PILLS
    : ALEVEL_ARCHETYPE_PILLS;
  const topicDisplay   = isGcse ? GCSE_TOPIC_DISPLAY   : isEnvSci ? ENV_SCIENCE_TOPIC_DISPLAY   : ALEVEL_TOPIC_DISPLAY;
  const baseTopicColors = isGcse ? GCSE_TOPIC_COLORS    : isEnvSci ? ENV_SCIENCE_TOPIC_COLORS    : ALEVEL_TOPIC_COLORS;
  const baseTopicBadges = isGcse ? GCSE_TOPIC_BADGES    : isEnvSci ? ENV_SCIENCE_TOPIC_BADGES    : ALEVEL_TOPIC_BADGES;
  // Proxy lookups so GCSE Science topics ("B1 Cell biology") fall back to a
  // subject-prefix colour without needing an explicit entry per topic key.
  const topicColors = new Proxy(baseTopicColors, {
    get(target, key) {
      if (target[key]) return target[key];
      return SCIENCE_TOPIC_COLOR_BY_PREFIX[String(key)[0]] ?? undefined;
    },
  });
  const topicBadges = new Proxy(baseTopicBadges, {
    get(target, key) {
      if (target[key]) return target[key];
      return SCIENCE_TOPIC_BADGE_BY_PREFIX[String(key)[0]] ?? undefined;
    },
  });

  const isGcseScience = isGcse && GCSE_SCIENCE_SUBJECTS.has(gcseSubject);
  const visible   = activeQuestions.filter((q) => {
    if (selectedArchetype !== 'past_papers') return q.archetype === selectedArchetype;
    // GCSE Science past-paper questions use subject-specific archetypes
    // (structured / short_answer / calculation / extended_6mark / mcq /
    // complete_figure / practical_method); psychology uses psych_*.
    if (isGcseScience) return typeof q.archetype === 'string';
    return typeof q.archetype === 'string' && q.archetype.startsWith('psych_');
  });
  const allTopics = [...new Set(visible.map((q) => q.topic))];
  if (isGcseScience) {
    allTopics.sort((a, b) => gcseScienceTopicOrder(a) - gcseScienceTopicOrder(b));
  }

  const [expandedTopics, setExpandedTopics] = useState(
    allTopics.reduce((acc, t) => ({ ...acc, [t]: false }), {})
  );
  const [expandedSections, setExpandedSections] = useState(
    ENV_SCIENCE_SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: false }), {})
  );
  const [footerMenuOpen, setFooterMenuOpen] = useState(false);

  // Reset expanded state when archetype or exam level changes
  useEffect(() => {
    setExpandedTopics(allTopics.reduce((acc, t) => ({ ...acc, [t]: false }), {}));
    setExpandedSections(ENV_SCIENCE_SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: false }), {}));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArchetype, examLevel, gcseSubject, aLevelSubject]);

  const toggleTopic = (topic) => {
    setExpandedTopics((prev) => ({ ...prev, [topic]: !prev[topic] }));
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const grouped = visible.reduce((acc, q) => {
    if (!acc[q.topic]) acc[q.topic] = [];
    acc[q.topic].push(q);
    return acc;
  }, {});

  // GCSE Science: bucket by topic → paper so the sidebar renders each topic
  // under the paper it "belongs to" in the AQA spec (e.g. B1 Cell biology on
  // Paper 1 only), not necessarily the paper an individual leaf came from.
  // Each topic's home paper is the paper_number where most of its leaves sit
  // — synoptic specimen leaves with a primary B1 spec_ref still bucket under
  // Paper 1 even when they were printed on Paper 2 of the specimen exam.
  const paperGroups = [];
  if (isGcseScience) {
    const topicVotes = new Map();
    for (const q of visible) {
      if (!topicVotes.has(q.topic)) topicVotes.set(q.topic, new Map());
      const votes = topicVotes.get(q.topic);
      const p = q.paper_number ?? 0;
      votes.set(p, (votes.get(p) ?? 0) + 1);
    }
    const topicHomePaper = new Map();
    for (const [topic, votes] of topicVotes) {
      // Highest vote count wins; ties break to the lower paper number.
      const winner = [...votes.entries()].sort(([pa, na], [pb, nb]) => nb - na || pa - pb)[0][0];
      topicHomePaper.set(topic, winner);
    }

    const byPaper = new Map();
    for (const q of visible) {
      const p = topicHomePaper.get(q.topic) ?? q.paper_number ?? 0;
      if (!byPaper.has(p)) byPaper.set(p, new Map());
      const topics = byPaper.get(p);
      if (!topics.has(q.topic)) topics.set(q.topic, []);
      topics.get(q.topic).push(q);
    }
    const sortedPapers = [...byPaper.keys()].sort((a, b) => a - b);
    for (const paper of sortedPapers) {
      const topics = [...byPaper.get(paper).entries()]
        .sort(([a], [b]) => gcseScienceTopicOrder(a) - gcseScienceTopicOrder(b))
        .map(([topic, qs]) => ({ topic, qs }));
      paperGroups.push({ paper, topics });
    }
  }

  // A-Level Environmental Science: bucket by paper_number, then by spec section.
  const envSciPaperGroups = [];
  if (isEnvSci) {
    const byPaper = new Map();
    for (const q of visible) {
      const p = q.paper_number ?? 1;
      if (!byPaper.has(p)) byPaper.set(p, []);
      byPaper.get(p).push(q);
    }
    const sortedPapers = [...byPaper.keys()].sort((a, b) => a - b);
    for (const paper of sortedPapers) {
      const paperQs = byPaper.get(paper);
      const sectionsForPaper = ENV_SCIENCE_SECTIONS
        .map((section) => ({
          ...section,
          qs: paperQs.filter((q) => (q.topic ?? '').split('.').slice(0, 2).join('.') === section.key),
        }))
        .filter((section) => section.qs.length > 0);
      envSciPaperGroups.push({ paper, sections: sectionsForPaper });
    }
  }

  const alevelSubtitleMap = { outline_4: '4-Mark Outline', data_6: '6-Mark Data', assess_6: '6-Mark Assess', scaffold_9: '9-Mark Scaffold', essay_20: '20-Mark Essay' };
  const gcseSubtitleMap   = { outline_4: '4-Mark Outline', assess_6: '6-Mark Evaluate', past_papers: 'Past Papers (2022–2024)' };
  const subtitleMap = isGcse ? gcseSubtitleMap : alevelSubtitleMap;

  return (
    <aside className="w-full lg:w-72 h-full bg-[#0d1117] border-r border-[#30363d] flex flex-col overflow-hidden">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="px-4 py-3 border-b border-[#30363d]">
        {/* Breadcrumb + inline level toggle */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold tracking-widest uppercase ${isGcse ? 'text-[#f85149]' : 'text-[#58a6ff]'}`}>AQA</span>
            <span className="text-[#30363d]">/</span>
            <span className="text-[#8b949e] text-xs tracking-wide">{isGcse ? 'GCSE' : 'A-Level'}</span>
          </div>
          <div className="flex items-center gap-0.5 p-0.5 bg-[#161b22] border border-[#30363d] rounded">
            <button
              onClick={() => onExamLevelChange?.('a-level')}
              className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded transition-colors ${!isGcse ? 'bg-[#21262d] text-[#58a6ff]' : 'text-[#484f58] hover:text-[#8b949e]'}`}
            >A-Lvl</button>
            <button
              onClick={() => onExamLevelChange?.('gcse')}
              className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded transition-colors ${isGcse ? 'bg-[#21262d] text-pink-400' : 'text-[#484f58] hover:text-[#8b949e]'}`}
            >GCSE</button>
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <h1 className="text-[#e6edf3] text-sm font-semibold leading-tight flex-shrink-0">Tutor Agent</h1>
          <span className="text-[#8b949e] text-xs truncate">
            {isGcse
              ? (GCSE_SUBJECTS_WITH_PAST_PAPERS.has(gcseSubject) ? (subtitleMap[selectedArchetype] ?? 'Practice') : 'MCQ Practice')
              : (hasPastPapers ? (subtitleMap[selectedArchetype] ?? 'Practice') : 'MCQ Practice')}
          </span>
        </div>
      </div>

      {/* ── A-Level subject selector (hidden in GCSE mode) ──────────────── */}
      {!isGcse && (
        <div className="px-4 py-2 border-b border-[#30363d]">
          <div className="flex gap-1 overflow-x-auto [&::-webkit-scrollbar]:hidden">
            {Object.entries(ALEVEL_SUBJECTS).map(([key, { label, badge, dot, activeBorder }]) => {
              const isActive = aLevelSubject === key;
              return (
                <button
                  key={key}
                  onClick={() => onALevelSubjectChange?.(key)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs font-mono transition-colors ${
                    isActive
                      ? `${badge} ${activeBorder} font-semibold`
                      : 'border-[#30363d] text-[#484f58] hover:border-[#484f58] hover:text-[#8b949e]'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActive ? dot : 'bg-[#30363d]'}`} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── GCSE subject selector (hidden in A-Level mode) ───────────────── */}
      {isGcse && (
        <div className="px-4 py-2 border-b border-[#30363d]">
          <div className="flex flex-col gap-1">
            {Object.entries(GCSE_SUBJECTS).map(([key, { label, badge, dot, activeBorder }]) => {
              const isActive = gcseSubject === key;
              return (
                <button
                  key={key}
                  onClick={() => onGcseSubjectChange?.(key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded border text-xs font-mono transition-colors text-left ${
                    isActive
                      ? `${badge} ${activeBorder} font-semibold`
                      : 'border-[#30363d] text-[#484f58] hover:border-[#484f58] hover:text-[#8b949e]'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActive ? dot : 'bg-[#30363d]'}`} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Model selector ───────────────────────────────────────────────── */}
      <div className="px-4 py-2 border-b border-[#30363d]">
        <div className="flex items-center gap-0.5 p-0.5 bg-[#161b22] border border-[#30363d] rounded-lg">
          {MODEL_OPTIONS.map(({ id }) => {
            const isActive = activeProvider === id;
            return (
              <button
                key={id}
                onClick={() => onProviderChange(id)}
                className={`flex-1 text-[10px] font-mono font-semibold py-1 rounded-md transition-colors ${
                  isActive ? 'bg-[#21262d] text-[#58a6ff]' : 'text-[#484f58] hover:text-[#8b949e]'
                }`}
              >
                {id === 'claude' ? 'Claude' : 'Gemini'}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Archetype selector (hidden for MCQ-only subjects) ───────────── */}
      {hasPastPapers && (
      <div className="px-4 py-2 border-b border-[#30363d]">
        <div className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden">
          {archetypePills.map((pill) => (
            <button
              key={pill.id}
              onClick={() => onArchetypeChange(pill.id)}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs font-semibold font-mono transition-colors ${
                selectedArchetype === pill.id ? pill.active : pill.inactive
              }`}
            >
              <span className="text-sm font-bold leading-none">{pill.marks}</span>
              <span>{pill.label}</span>
            </button>
          ))}
        </div>

        {/* Guided / Exam mode toggle — scaffold_9 only (A-Level mode) */}
        {selectedArchetype === 'scaffold_9' && !isGcse && (
          <div className="mt-2 flex items-center justify-between bg-[#161b22] border border-[#30363d] rounded-lg px-3 py-1.5">
            <span className="text-[10px] font-mono text-[#8b949e] uppercase tracking-wider">Mode</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => onGuidedModeChange?.(true)}
                className={`text-[10px] px-2 py-0.5 rounded font-mono transition-colors ${
                  guidedMode
                    ? 'bg-[#58a6ff] text-[#0d1117] font-semibold'
                    : 'text-[#8b949e] hover:text-[#e6edf3]'
                }`}
              >
                Guided
              </button>
              <button
                onClick={() => onGuidedModeChange?.(false)}
                className={`text-[10px] px-2 py-0.5 rounded font-mono transition-colors ${
                  !guidedMode
                    ? 'bg-[#d29922] text-[#0d1117] font-semibold'
                    : 'text-[#8b949e] hover:text-[#e6edf3]'
                }`}
              >
                Exam
              </button>
            </div>
          </div>
        )}
      </div>
      )} {/* end archetype selector */}

      {/* ── Question list or MCQ-only prompt ─────────────────────────────── */}
      {!hasPastPapers ? (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6 py-8 text-center">
          {(() => {
            const subjectMeta = isGcse
              ? (GCSE_SUBJECTS[gcseSubject] ?? {})
              : (ALEVEL_SUBJECTS[aLevelSubject] ?? {});
            return (
              <button
                onClick={onOpenQuiz}
                className={`w-full flex flex-col items-center gap-3 px-5 py-6 rounded-lg border transition-all hover:bg-[#161b22] ${
                  subjectMeta.activeBorder ?? 'border-[#30363d]'
                } border-opacity-40 hover:border-opacity-80`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  subjectMeta.badge ?? 'bg-[#21262d]'
                }`}>
                  <svg className={`w-5 h-5 ${subjectMeta.color ?? 'text-[#8b949e]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className={`text-xs font-semibold mb-1 ${subjectMeta.color ?? 'text-[#e6edf3]'}`}>
                    MCQ Question Bank
                  </p>
                  <p className="text-[#484f58] text-[11px] leading-relaxed">
                    Click to start practising {subjectMeta.label ?? 'this subject'} questions by topic
                  </p>
                </div>
              </button>
            );
          })()}
        </div>
      ) : isEnvSci ? (
      <nav className="flex-1 min-h-0 px-2 py-3 space-y-1 overflow-y-auto">
        {envSciPaperGroups.map(({ paper, sections: paperSections }) => (
          <div key={`envsci-paper-${paper}`} className="mb-3">
            <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-[#484f58]">
              Paper {paper}
            </div>
            {paperSections.map(({ key: sectionKey, label: sectionLabel, color: sectionColor, badge: sectionBadge, qs }) => {
          const expandKey = `envsci-p${paper}__${sectionKey}`;
          return (
            <div key={expandKey}>
              <button
                onClick={() => toggleSection(expandKey)}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded text-left hover:bg-[#161b22] transition-colors"
              >
                <span className={`text-xs font-semibold uppercase tracking-wider ${sectionColor}`}>
                  {sectionLabel}
                </span>
                <svg
                  className={`w-3 h-3 text-[#8b949e] flex-shrink-0 transition-transform ${expandedSections[expandKey] ? 'rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {expandedSections[expandKey] && (
                <div className="ml-2 space-y-0.5 mt-0.5">
                  {qs.map((q, i) => {
                    const bestScore  = questionBestScores[q.id];
                    const hasScore   = bestScore !== undefined;
                    const isFullMark = hasScore && bestScore === q.marks;
                    const isSelected = selectedQuestion?.id === q.id;
                    return (
                      <button
                        key={q.id}
                        onClick={() => onSelect(q)}
                        className={`w-full text-left px-3 py-2 rounded text-xs leading-snug transition-all border ${
                          isSelected
                            ? `${sectionBadge} font-medium`
                            : isFullMark
                            ? 'text-[#3fb950] border-[#3fb950]/20 bg-[#3fb950]/5 hover:bg-[#3fb950]/10'
                            : hasScore
                            ? 'text-[#d29922] border-[#d29922]/20 bg-[#d29922]/5 hover:bg-[#d29922]/10'
                            : 'text-[#8b949e] border-transparent hover:bg-[#161b22] hover:text-[#e6edf3]'
                        }`}
                      >
                        <span className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] opacity-60">
                            {formatQuestionLabel(q, i)}
                          </span>
                          <span className="flex items-center gap-1 flex-shrink-0">
                            {hasScore && (
                              <span className={`text-[10px] font-mono font-semibold ${isFullMark ? 'text-[#3fb950]' : 'text-[#d29922]'}`}>
                                {bestScore}/{q.marks}
                              </span>
                            )}
                            {isFullMark && (
                              <svg className="w-3 h-3 text-[#3fb950]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </span>
                        </span>
                        {q.question_text.length > 70 ? q.question_text.slice(0, 70) + '…' : q.question_text}
                      </button>
                    );
                  })}
                  {qs.length === 0 && (
                    <p className="px-3 py-2 text-[#484f58] text-xs italic">No questions yet.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
          </div>
        ))}

        {visible.length === 0 && (
          <p className="px-3 py-4 text-[#484f58] text-xs italic">No questions yet for this archetype.</p>
        )}
      </nav>
      ) : (
      <nav className="flex-1 min-h-0 px-2 py-3 space-y-1 overflow-y-auto">
        {(() => {
          // Build the list of (topic, qs, expandKey) entries to render. For GCSE
          // Science we group by paper and inject a "PAPER N" heading; otherwise
          // we render the flat topic list as before.
          const renderTopicBlock = (topic, qs, expandKey) => (
            <div key={expandKey}>
              <button
                onClick={() => toggleTopic(expandKey)}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded text-left group hover:bg-[#161b22] transition-colors"
              >
                <span className={`text-xs font-semibold uppercase tracking-wider ${topicColors[topic] || 'text-[#8b949e]'}`}>
                  {topicDisplay[topic] || topic}
                </span>
                <svg
                  className={`w-3 h-3 text-[#8b949e] transition-transform ${expandedTopics[expandKey] ? 'rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {expandedTopics[expandKey] && (
                <div className="ml-2 space-y-0.5 mt-0.5">
                  {qs.map((q, i) => {
                    const bestScore  = questionBestScores[q.id];
                    const hasScore   = bestScore !== undefined;
                    const isFullMark = hasScore && bestScore === q.marks;
                    const isSelected = selectedQuestion?.id === q.id;
                    return (
                      <button
                        key={q.id}
                        onClick={() => onSelect(q)}
                        className={`w-full text-left px-3 py-2 rounded text-xs leading-snug transition-all border ${
                          isSelected
                            ? `${topicBadges[topic] || 'bg-slate-400/10 text-slate-400 border-slate-400/30'} font-medium`
                            : isFullMark
                            ? 'text-[#3fb950] border-[#3fb950]/20 bg-[#3fb950]/5 hover:bg-[#3fb950]/10'
                            : hasScore
                            ? 'text-[#d29922] border-[#d29922]/20 bg-[#d29922]/5 hover:bg-[#d29922]/10'
                            : 'text-[#8b949e] border-transparent hover:bg-[#161b22] hover:text-[#e6edf3]'
                        }`}
                      >
                        <span className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] opacity-60">
                            {formatQuestionLabel(q, i)}
                          </span>
                          <span className="flex items-center gap-1 flex-shrink-0">
                            {hasScore && (
                              <span className={`text-[10px] font-mono font-semibold ${isFullMark ? 'text-[#3fb950]' : 'text-[#d29922]'}`}>
                                {bestScore}/{q.marks}
                              </span>
                            )}
                            {isFullMark && (
                              <svg className="w-3 h-3 text-[#3fb950]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </span>
                        </span>
                        {q.question_text.length > 70 ? q.question_text.slice(0, 70) + '…' : q.question_text}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );

          if (isGcseScience) {
            return paperGroups.map(({ paper, topics }) => (
              <div key={`paper-${paper}`} className="mb-3">
                <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-[#484f58]">
                  Paper {paper}
                </div>
                {topics.map(({ topic, qs }) => renderTopicBlock(topic, qs, `p${paper}__${topic}`))}
              </div>
            ));
          }

          return allTopics.map((topic) => renderTopicBlock(topic, grouped[topic] ?? [], topic));
        })()}

        {visible.length === 0 && (
          <p className="px-3 py-4 text-[#484f58] text-xs italic">No questions yet for this archetype.</p>
        )}
      </nav>
      )} {/* end question list conditional */}

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <div className="px-4 py-2 border-t border-[#30363d] space-y-1.5">
        {/* Points summary bar */}
        <div
          onClick={onOpenScoreReview}
          className="flex items-center justify-between px-3 py-1.5 rounded border border-[#d29922]/30 bg-[#d29922]/5 cursor-pointer hover:border-[#d29922]/60 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-[#d29922] text-sm">⭐</span>
            {totalPoints === null ? (
              <span className="inline-block w-12 h-3 bg-[#30363d] rounded animate-pulse" />
            ) : (
              <span key={totalPoints} className="text-[#e6edf3] text-xs font-mono font-semibold animate-fade-in">{totalPoints.toLocaleString()} pts</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {todayPoints !== null && (
              <span className="text-[10px] font-mono text-[#e6edf3]" title="Points earned today">+{todayPoints.toLocaleString()} pts</span>
            )}
            {currentStreak >= 3 && (
              <span className="text-[10px] font-mono text-[#d29922]">
                {currentStreak >= 7 ? '🔥' : '✨'} {currentStreak}d
              </span>
            )}
          </div>
        </div>
        {/* Primary action + overflow menu */}
        <div className="flex gap-1.5">
          <button
            onClick={onOpenQuiz}
            className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff]/40 hover:bg-[#58a6ff]/5 transition-colors text-xs font-mono"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>MCQ Quiz</span>
          </button>
          <div className="relative">
            <button
              onClick={() => setFooterMenuOpen((v) => !v)}
              className={`flex items-center justify-center w-8 h-full rounded border text-xs font-mono transition-colors ${
                footerMenuOpen
                  ? 'border-[#58a6ff]/40 bg-[#58a6ff]/10 text-[#58a6ff]'
                  : 'border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#484f58]'
              }`}
              aria-label="More options"
            >
              ···
            </button>
            {footerMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setFooterMenuOpen(false)} />
                <div className="absolute bottom-full right-0 mb-1 z-20 bg-[#161b22] border border-[#30363d] rounded-lg py-1 min-w-[168px] shadow-xl">
                  <button
                    onClick={() => { onOpenLeaderboard(); setFooterMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#21262d] transition-colors text-xs font-mono"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    Leaderboard
                  </button>
                  {!isGcse && (
                    <button
                      onClick={() => { onOpenExamHistory(); setFooterMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#21262d] transition-colors text-xs font-mono"
                    >
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      My Exams
                    </button>
                  )}
                  {!isGcse && (
                    <button
                      onClick={() => { onOpenMockExam(); setFooterMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#21262d] transition-colors text-xs font-mono"
                    >
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Mock Exam
                    </button>
                  )}
                  <button
                    onClick={() => { onOpenHistory(); setFooterMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#21262d] transition-colors text-xs font-mono"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Results Ledger
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => { onOpenDashboard(); setFooterMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#21262d] transition-colors text-xs font-mono"
                    >
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Usage Dashboard
                      {isTyping && (
                        <span className="ml-auto flex items-center gap-1 text-[#3fb950] text-[9px]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#3fb950] animate-pulse" />
                          active
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
